import { useUpdateSlideMutation } from "@api/slides-library";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useCallback, useState } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { handleAsyncOperation } from "@/shared";
import type { TSavedSlide } from "@/shared/types";

const DIALOG_ID = "rename-slide-dialog";

const SlideRenameDialog = ({ slide }: { slide: TSavedSlide }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(slide.title);
  const [updateSlide, { isLoading }] = useUpdateSlideMutation();
  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!name?.trim() || isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        await updateSlide({
          id: slide._id,
          folderId: slide.folderId ?? "",
          title: name,
        }).unwrap();
        toast.success("Slide renamed");
        handleClose();
      },
      onError: () => toast.error("Failed to rename slide"),
    });
  }, [updateSlide, handleClose, isLoading, slide._id, slide.folderId, name]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit],
  );

  return (
    <DialogBase>
      <DialogBase.Header title="Rename slide" onClose={handleClose} />
      <DialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Slide name
        </span>
        <Input
          placeholder="Add your slide name here..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim() || name === slide.title}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlideRenameDialog.id = DIALOG_ID;
export { SlideRenameDialog };
